"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { RiCheckLine, RiCloseLine, RiRefreshLine } from "react-icons/ri";
import ReactMarkdown from "react-markdown";
import AIBadge from "@/components/ui/ai-badge";
import Button from "@/components/ui/button";
import Tooltip from "@/components/ui/tooltip";
import { useAIGenerationWithStream } from "@/hooks/use-ai-generation-with-stream";
import { cx, focusInput, hasErrorInput } from "@/lib/tailwind-utils";
interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  hasError?: boolean;
  errorMessage?: string;
  showMarkdown?: boolean;
  /** If provided, enables AI suggestion features. Function returns an async generator. */
  onGenerateAI?: (currentValue: string, feedback?: string, previousGeneration?: string) => AsyncGenerator<string>;
  /** Tooltip text to show when AI suggestions are disabled (e.g., when name is empty) */
  aiDisabledTooltip?: string;
  /** Called with the AI-generated text during streaming, useful for live preview */
  onAITextUpdate?: (text: string) => void;
}
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(({
  className,
  hasError,
  errorMessage,
  autoComplete = "on",
  value,
  showMarkdown = false,
  disabled,
  onGenerateAI,
  onChange,
  aiDisabledTooltip,
  onAITextUpdate,
  rows,
  ...props
}: TextareaProps, forwardedRef) => {
  const [isEditing, setIsEditing] = useState(!disabled);
  const internalRef = React.useRef<HTMLTextAreaElement>(null);
  const textareaRef = forwardedRef as React.RefObject<HTMLTextAreaElement> || internalRef;

  // Approximate min-height from rows (each row ~1.5rem line-height + padding)
  const aiMinHeight = rows ? `${rows * 1.5}rem` : "100px";

  // AI state (only used if onGenerateAI is provided)
  const [suggestionMode, setSuggestionMode] = useState(false);
  const [promptMode, setPromptMode] = useState(false);
  const [prompt, setPrompt] = useState("");
  const promptRef = useRef("");
  const [feedback, setFeedback] = useState("");
  const [feedbackHistory, setFeedbackHistory] = useState<string[]>([]);
  const [editedText, setEditedText] = useState("");
  const aiGeneration = useAIGenerationWithStream({
    onTextUpdate: text => {
      setEditedText(text);
      onAITextUpdate?.(text);
    },
    transformText: text_0 => text_0.replace(/^```\w*\n?/, "").replace(/\n?```\s*$/, "")
  });
  const aiIsGenerating = aiGeneration.isGenerating;
  const aiGeneratedText = aiGeneration.generatedText;
  const aiError = aiGeneration.error;
  const aiCancel = aiGeneration.cancel;
  const {
    generate: aiGenerate,
    regenerate: aiRegenerate,
    reset: aiResetHook
  } = aiGeneration;
  const aiReset = useCallback(() => {
    setEditedText("");
    aiResetHook();
  }, [aiResetHook]);

  // Sync edited text when generation updates
  useEffect(() => {
    if (onGenerateAI && aiGeneratedText) {
      setEditedText(aiGeneratedText);
    }
  }, [onGenerateAI, aiGeneratedText]);
  const handleGenerate = useCallback(async (initialPrompt?: string) => {
    if (!onGenerateAI) return;
    promptRef.current = initialPrompt || "";
    setPromptMode(false);
    setSuggestionMode(true);
    setFeedback("");
    setFeedbackHistory([]);
    setEditedText("");
    await aiGenerate(() => onGenerateAI(value as string || "", initialPrompt || undefined));
  }, [onGenerateAI, value, aiGenerate]);
  const handleSuggestClick = useCallback(() => {
    // If there's no existing content, generate immediately without asking for a prompt
    if (!(value as string)?.trim()) {
      handleGenerate();
    } else {
      setPromptMode(true);
      setPrompt("");
    }
  }, [value, handleGenerate]);
  const handleRetry = useCallback(async () => {
    if (!onGenerateAI) return;
    await aiGenerate(() => onGenerateAI(value as string || "", promptRef.current || undefined));
  }, [onGenerateAI, value, aiGenerate]);
  const handleRegenerate = useCallback(async () => {
    if (feedback.trim()) {
      setFeedbackHistory(prev => [...prev, feedback.trim()]);
    }
    if (!onGenerateAI) return;
    await aiRegenerate(onGenerateAI, value as string || "", feedback, editedText);
    setFeedback("");
  }, [feedback, onGenerateAI, value, editedText, aiRegenerate]);
  const handleAccept = useCallback(() => {
    // Trigger onChange with a properly constructed event
    if (onChange && textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.value = editedText;
      const event = {
        target: textarea,
        currentTarget: textarea,
        bubbles: true,
        cancelable: true,
        defaultPrevented: false,
        eventPhase: 0,
        isTrusted: false,
        nativeEvent: new Event("change", {
          bubbles: true
        }),
        preventDefault: () => {},
        stopPropagation: () => {},
        isDefaultPrevented: () => false,
        isPropagationStopped: () => false,
        persist: () => {},
        timeStamp: Date.now(),
        type: "change"
      } as React.ChangeEvent<HTMLTextAreaElement>;
      onChange(event);
    }
    setSuggestionMode(false);
    setFeedback("");
    setFeedbackHistory([]);
    setEditedText("");
    aiReset();
  }, [onChange, editedText, aiReset]);
  const handleAIKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (feedback.trim()) {
        handleRegenerate();
      } else {
        handleRetry();
      }
    }
  }, [feedback, handleRegenerate, handleRetry]);

  // If onGenerateAI is provided OR aiDisabledTooltip is provided, render with AI features
  if (onGenerateAI || aiDisabledTooltip) {
    return <div className={cx("overflow-hidden rounded-lg border border-border bg-background transition-all", suggestionMode && "shadow-sm")}>
          {/* Suggestion content — always mounted, toggled via hidden */}
          <div className={suggestionMode ? "" : "hidden"}>
            {aiError ? <div style={{
          minHeight: aiMinHeight
        }} className="p-3 text-body text-status-error-text">
                Error: {aiError}
              </div> : aiIsGenerating ? <div style={{
          minHeight: aiMinHeight
        }} className="whitespace-pre-wrap p-3 text-body text-foreground">
                {aiGeneratedText || <span className="text-foreground-muted">Generating...</span>}
                <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-primary" />
              </div> : <textarea value={editedText} onChange={e_0 => setEditedText(e_0.target.value)} style={{
          minHeight: aiMinHeight
        }} className={cx("w-full resize-none rounded-none border-0 px-3 py-1.5 text-body shadow-none outline-none ring-0 focus:shadow-none focus:outline-none focus:ring-0", className)} placeholder="AI generated content will appear here..." />}
          </div>

          {/* Normal textarea — always mounted, toggled via hidden */}
          <textarea ref={textareaRef} value={value} onChange={onChange} disabled={disabled} rows={rows} className={cx("min-h-[100px] w-full resize-none rounded-none border-0 px-3 py-1.5 text-body shadow-none outline-none ring-0 focus:shadow-none focus:outline-none focus:ring-0", suggestionMode && "hidden", className)} autoComplete={autoComplete} {...props} />

          {/* Prompt input — shown before first generation */}
          <div className={promptMode ? "" : "hidden"}>
            <div className="border-t border-border bg-background-secondary/50">
              <div className="flex items-center justify-between px-2 py-1.5">
                <div className="flex items-center gap-1.5">
                  <AIBadge size={14} />
                  <span className="text-caption-strong text-primary">
                    AI Suggestion
                  </span>
                </div>
                <Tooltip content="Cancel" triggerAsChild>
                  <Button type="button" variant="ghost" size="icon-sm" onClick={() => {
                setPromptMode(false);
                setPrompt("");
              }} className="text-foreground-secondary hover:text-status-error-text">
                    <RiCloseLine className="h-4 w-4" />
                  </Button>
                </Tooltip>
              </div>
              <input type="text" placeholder="Describe what you want to generate and press Enter..." value={prompt} onChange={e_1 => setPrompt(e_1.target.value)} onKeyDown={e_2 => {
            if (e_2.key === "Enter" && prompt.trim()) {
              e_2.preventDefault();
              handleGenerate(prompt.trim());
            }
          }} autoComplete="off" autoFocus style={{
            outline: "none",
            boxShadow: "none"
          }} className="w-full appearance-none border-0 border-t border-border bg-transparent px-3 py-2 text-body text-foreground-secondary placeholder:text-foreground-muted" />
            </div>
          </div>

          {/* AI Toolbar — inline, matching TiptapEditor pattern */}
          <div className={suggestionMode ? "" : "hidden"}>
            <div className="border-t border-border bg-background-secondary/50">
              <div className="flex items-center justify-between px-2 py-1.5">
                <div className="flex items-center gap-1.5">
                  <AIBadge size={14} />
                  <span className="text-caption-strong text-primary">
                    AI Suggestion
                  </span>
                  {aiIsGenerating && <span className="text-caption text-foreground-muted">
                      generating...
                    </span>}
                  {aiError && <span className="text-caption text-status-error-text">
                      failed
                    </span>}
                  {feedbackHistory.length > 0 && !aiIsGenerating && <span className="text-caption text-foreground-muted">
                      · {feedbackHistory.length} revision
                      {feedbackHistory.length > 1 ? <span>{"s"}</span> : <span>{""}</span>}
                    </span>}
                </div>
                <div className="flex items-center gap-0.5">
                  {!aiIsGenerating && !!editedText && !aiError && <Tooltip content="Accept" triggerAsChild>
                      <Button type="button" variant="ghost" size="icon-sm" onClick={handleAccept} className="text-primary hover:bg-hover hover:text-primary">
                        <RiCheckLine className="h-4 w-4" />
                      </Button>
                    </Tooltip>}
                  {!aiIsGenerating && <Tooltip content="Regenerate" triggerAsChild>
                      <Button type="button" variant="ghost" size="icon-sm" onClick={handleRetry} className="text-foreground-secondary hover:text-foreground">
                        <RiRefreshLine className="h-4 w-4" />
                      </Button>
                    </Tooltip>}
                  <Tooltip content="Cancel" triggerAsChild>
                    <Button type="button" variant="ghost" size="icon-sm" onClick={() => {
                  aiCancel();
                  setSuggestionMode(false);
                  setFeedbackHistory([]);
                  setEditedText("");
                  aiReset();
                }} className="text-foreground-secondary hover:text-status-error-text">
                      <RiCloseLine className="h-4 w-4" />
                    </Button>
                  </Tooltip>
                </div>
              </div>
              {!aiIsGenerating && <input type="text" placeholder="Add feedback and press Enter to regenerate..." value={feedback} onChange={e_3 => setFeedback(e_3.target.value)} onKeyDown={handleAIKeyDown} autoComplete="off" style={{
            outline: "none",
            boxShadow: "none"
          }} className="w-full appearance-none border-0 border-t border-border bg-transparent px-3 py-2 text-body text-foreground-secondary placeholder:text-foreground-muted" />}
            </div>
          </div>

          {/* Suggest button — always mounted, toggled via hidden */}
          {!disabled && <div className={suggestionMode || promptMode ? "hidden" : ""}>
              <Tooltip content={onGenerateAI ? undefined : aiDisabledTooltip || "Enter a name to enable AI suggestions"} triggerAsChild>
                <button type="button" onClick={handleSuggestClick} disabled={!onGenerateAI} className={cx("flex w-full items-center gap-1.5 border-t border-border bg-background-secondary/50 px-2 py-1.5 transition-colors", onGenerateAI ? "hover:bg-hover/50" : "cursor-not-allowed opacity-50")}>
                  <AIBadge size={14} />
                  <span className={cx("text-caption-strong", onGenerateAI ? "text-primary" : "text-foreground-muted")}>
                    Suggest...
                  </span>
                </button>
              </Tooltip>
            </div>}

          {hasError && errorMessage && <p className="px-3 py-1 text-caption text-status-error-text">
              {errorMessage}
            </p>}
        </div>;
  }

  // Standard textarea without AI features
  return <>
        {!isEditing && showMarkdown ? <div onClick={() => {
      if (!disabled) {
        setIsEditing(true);
        setTimeout(() => {
          textareaRef.current?.focus();
        }, 0);
      }
    }} className={cx("prose prose-sm max-w-none rounded-md border border-border !bg-transparent px-3 py-1 backdrop-blur-sm", "h-full w-full space-y-4 overflow-y-auto", !disabled ? "cursor-text hover:border-border" : "bg-muted", !value && "text-muted-foreground", className)}>
            <ReactMarkdown components={{
        h1: "h2",
        h2: "h3",
        h3: ({
          children
        }: {
          children: React.ReactNode;
        }) => <p className="whitespace-pre-wrap">{children}</p>,
        ul: ({
          children: children_0
        }: {
          children: React.ReactNode;
        }) => <ul className="list-disc pl-5">{children_0}</ul>,
        li: ({
          children: children_1
        }: {
          children: React.ReactNode;
        }) => <li className="text-body text-foreground-secondary">
                      {children_1}
                    </li>
      } as any}>
              {value as string || props.placeholder || ""}
            </ReactMarkdown>
          </div> : <textarea ref={textareaRef} value={value} onChange={onChange} className={cx(
    // base
    "h-full w-full rounded-md border border-border px-3 py-1.5 text-body shadow-sm outline-none transition-colors",
    // text color
    "text-foreground",
    // background color
    "bg-background/0",
    // placeholder color
    "placeholder-foreground-muted",
    // disabled
    "disabled:border-border disabled:bg-muted disabled:text-foreground-muted",
    // focus
    focusInput,
    // error
    hasError ? hasErrorInput : "", className)} disabled={disabled} rows={rows} autoComplete={autoComplete} onBlur={() => setIsEditing(false)} {...props} />}
        {hasError && <p id="error-message" className="-mt-0.5 text-caption text-status-error-text">
            {errorMessage}
          </p>}
      </>;
});
Textarea.displayName = "Textarea";
export default Textarea;
export { Textarea, type TextareaProps };